import dynamic from "next/dynamic";

import { Loader } from "components/customElements/loader";
import { useApi } from "hooks/useApi";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { colors } from "consts";

const TimeSeriesChart = dynamic(
  () => import("components/AmChart/TimeSeriesChart"),
  {
    ssr: false,
  }
);

const egsUrl = (id: string) => {
  const url = new ApiEndpoint(`/api/v2/entities/${id}/owl-analytics`);
  url.searchParams.append("sort", "-date");
  return url.href;
};

interface Props {
  entity: any;
}

const ESG = ({ entity }: Props) => {
  const { data: esg, isValidating } = useApi(egsUrl(entity?.id), baseFetcher);

  const chartData = (esg?.data || [])
    .filter((item: any) => item.esgRegionalSectorPercentile)
    .map((item: any) => ({
      ...item,
      date: item.date,
      esg: Math.round(parseFloat(item.esg)),
      esgRegionalSectorPercentile:
        Math.round(parseFloat(item.esgRegionalSectorPercentile || "0") * 100) /
        100,
    }))
    .reverse();

  const latest = chartData?.[chartData?.length - 1];

  const ESGChartConfig = {
    charts: [
      {
        type: "column",
        name: "Percentile",
        fieldName: "esgRegionalSectorPercentile",
        color: colors.jade,
        forceMax: 1,
        tooltipFormat: "{name}: {valueY.formatNumber('#%')}",
      },
      {
        type: "line",
        name: "Score",
        fieldName: "esg",
        position: "right",
        color: colors.inkPink,
        forceMax: 100,
        tooltipFormat: "{name}: {valueY.formatNumber('#')}",
      },
    ],
    isColumnValuePercent: true,
    isLineValuePercent: false,
  };

  const chartClass = "w-full h-[400px] mb-[80px]";

  if (isValidating) return <Loader />;

  return (
    <>
      <div className="mb-[15px] flex justify-between">
        <div className="w-[120px] m-[5px] p-[10px] border border-solid border-black/10 rounded text-center">
          <div className="text-[#999] text-[12px] mb-[5px]">Percentile</div>
          <div className="text-[1.5em] font-medium">
            {latest?.esgRegionalSectorPercentile * 100}%
          </div>
          <div className="text-[12px]">ESG SCore: {latest?.esg}</div>
        </div>

        <div className="w-[120px] m-[5px] p-[10px] border border-solid border-black/10 rounded text-center">
          <div className="text-[#999] text-[12px] mb-[5px]">Rank</div>
          <div className="text-[1.5em] font-medium">
            {Number.parseInt(latest?.esgRegionalSectorRank)}
          </div>
          <div className="text-[12px]">
            vs {Number.parseInt(latest?.regionSectorCount)} peers
          </div>
        </div>
      </div>

      <TimeSeriesChart
        chartData={chartData}
        chartConfig={ESGChartConfig}
        className={chartClass}
      />

      <div className="text-[#999] text-[12px]">
        Data powered by{" "}
        <a
          href="http://owlanalytics.net"
          target="_blank"
          rel="nofollow noreferrer"
          className="text-smart-teal"
        >
          OWL Analytics
        </a>
        , 3 months delay
      </div>
    </>
  );
};

export default ESG;
